import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle, MessageSquare, Star } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

type FeedbackType = 'general' | 'bug' | 'feature_request' | 'account_deletion' | 'complaint' | 'praise';

const validTypes: FeedbackType[] = ['general', 'bug', 'feature_request', 'account_deletion', 'complaint', 'praise'];

function FeedbackPage() {
  const { t, toggleLocale } = useTranslation();
  const [searchParams] = useSearchParams();
  const typeFromUrl = searchParams.get('type') as FeedbackType | null;

  const [feedbackType, setFeedbackType] = useState<FeedbackType>(
    typeFromUrl && validTypes.includes(typeFromUrl) ? typeFromUrl : 'general'
  );
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const feedbackTypes: { value: FeedbackType; label: string }[] = [
    { value: 'general', label: t.feedback.types.general },
    { value: 'feature_request', label: t.feedback.types.feature_request },
    { value: 'bug', label: t.feedback.types.bug },
    { value: 'complaint', label: t.feedback.types.complaint },
    { value: 'praise', label: t.feedback.types.praise },
    { value: 'account_deletion', label: t.feedback.types.account_deletion },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!message.trim()) {
      setError(t.feedback.errors.messageRequired);
      return;
    }

    if (message.trim().length < 10) {
      setError(t.feedback.errors.messageTooShort);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: feedbackType,
          message: message.trim(),
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          rating: rating || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitted(true);
    } catch {
      setError(t.feedback.errors.submitFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t.feedback.success.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t.feedback.success.message}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.feedback.backToHome}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Nav */}
      <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-green-600"
          >
            <ArrowLeft className="w-5 h-5" />
            <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
            <span className="font-bold text-xl text-gray-900 dark:text-white">KeepNota</span>
          </Link>

          <button
            onClick={toggleLocale}
            className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            {t.flag} {t.lang.split('-')[0].toUpperCase()}
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t.feedback.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {t.feedback.subtitle}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Feedback Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t.feedback.typeLabel}
              </label>
              <div className="flex flex-wrap gap-2">
                {feedbackTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFeedbackType(type.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      feedbackType === type.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {t.feedback.messageLabel}
              </label>
              <textarea
                id="message"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t.feedback.messagePlaceholder}
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
              {error && (
                <p className="mt-2 text-sm text-red-500 dark:text-red-400">{error}</p>
              )}
            </div>

            {/* Rating (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t.feedback.ratingLabel}
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(rating === star ? null : star)}
                    className="p-1 transition-colors"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        rating && star <= rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Name (Optional) */}
            <Input
              label={t.feedback.nameLabel}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.feedback.namePlaceholder}
            />

            {/* Email (Optional) */}
            <Input
              label={t.feedback.emailLabel}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.feedback.emailPlaceholder}
              hint={t.feedback.emailHint}
            />

            {/* Submit */}
            <Button
              type="submit"
              size="lg"
              fullWidth
              isLoading={isSubmitting}
              rightIcon={<Send className="w-4 h-4" />}
            >
              {t.feedback.submit}
            </Button>
          </form>
        </div>

        {/* Back link */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
          >
            ← {t.feedback.backToHome}
          </Link>
        </div>
      </main>
    </div>
  );
}

export default FeedbackPage;
